import { Collapse, useModal } from '@shape-ds/components';
import { MouseEvent, useCallback, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GenericModal, ModalWidthEnum } from 'ui/GenericModal/GenericModal';
import { ActionsForm } from './ActionsForm';
import { ActionHeader } from './ActionHeader';
import { useFeatureFlag } from '@shape/feature-flag';

const ignoredFields = ['id', 'name', 'creation_date', 'type_case_action', 'status', 'deleted'];

interface Props {
  index: number;
  isStepAble: boolean;
  isStepRequired: boolean;
  onSubmit: (e?: React.BaseSyntheticEvent) => void;
}

export const SingleAction = ({ index, isStepAble, isStepRequired, onSubmit }: Props) => {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();
  const isDemoOTCEnabled = useFeatureFlag('demo-OTC');
  const { watch, control } = useFormContext();

  const fieldArray = useFieldArray({
    control,
    name: 'actions',
  });

  const actions = watch('actions');
  const currentAction = actions?.[index];

  const isNewAction = !currentAction?.id;
  const [isActionOpen, setIsActionOpen] = useState(isNewAction);

  const isDeleted = currentAction?.deleted || false;

  const isDisabled = isDemoOTCEnabled ? !isDemoOTCEnabled : !isStepAble || isDeleted;

  const toggleActionOpen = useCallback(() => setIsActionOpen((prev) => !prev), []);

  const deleteAction = (e: MouseEvent<HTMLElement>, actionIndex: number) => {
    e.stopPropagation();

    const isActionEmpty = Object.entries(currentAction).some(([key, value]) => {
      if (!ignoredFields.includes(key)) {
        return value && (!Array.isArray(value) || value.length > 0);
      }
      return false;
    });

    if (isNewAction) {
      if (isActionEmpty) {
        fieldArray.remove(actionIndex);
      } else {
        openModal(
          <GenericModal
            title={t('case:messages.delete.title')}
            description={t('case:messages.delete.message')}
            width={ModalWidthEnum.MEDIUM}
            leftBtn={{ style: 'secondary', text: t('case:cancel'), onClick: closeModal }}
            rightBtn={{
              style: 'primary',
              text: t('case:confirm_delete'),
              onClick: () => {
                fieldArray.remove(actionIndex);
                closeModal();
              },
            }}
          />,
        );
      }
    } else {
      fieldArray.update(actionIndex, {
        ...actions[actionIndex],
        deleted: !actions[actionIndex].deleted,
      });
    }
  };

  return (
    <Collapse
      key={index}
      className="single-action-collapse"
      isOpen={isActionOpen}
      toggleIsOpen={toggleActionOpen}
      cursorPointerOnArrowOnly={isActionOpen}
      title={
        <ActionHeader
          isDisabled={isDisabled}
          isActionOpen={isActionOpen}
          isDeleted={isDeleted}
          isStepRequired={isStepRequired}
          deleteAction={(e) => deleteAction(e, index)}
          index={index}
          data-testid="action-header"
        />
      }
    >
      <ActionsForm
        index={index}
        isDisabled={isDisabled}
        isStepRequired={isStepRequired}
        onSubmit={onSubmit}
      />
    </Collapse>
  );
};
